/**
 * SlaCountdown — days/hours remaining against an SLA due-by timestamp.
 *
 * Per the design system: amber (warning) when within 24h, red (danger) when
 * breached, otherwise neutral. Surfaces "days remaining" at coarse range and
 * "Xh" when close, matching the wireframe SLA column.
 */
import type { CSSProperties } from 'react';
import { colors, tints } from '../theme';

export interface SlaCountdownProps {
  /** ISO timestamp the SLA is due by. */
  dueAt: string | null | undefined;
  style?: CSSProperties;
}

interface SlaState {
  label: string;
  fg: string;
  bg: string;
}

/** Classify SLA health from a due-by timestamp. Exported for reuse/testing. */
export function classifySla(dueAt: string | null | undefined): SlaState {
  if (!dueAt) return { label: 'No SLA', fg: colors.textSecondary, bg: tints.neutral };
  const ms = new Date(dueAt).getTime() - Date.now();
  if (Number.isNaN(ms)) return { label: '—', fg: colors.textSecondary, bg: tints.neutral };
  const hours = ms / 3_600_000;
  if (ms < 0) {
    const overH = Math.round(Math.abs(hours));
    const label = overH >= 48 ? `Breached ${Math.round(overH / 24)}d` : `Breached ${overH}h`;
    return { label, fg: tints.dangerText, bg: tints.danger };
  }
  if (hours <= 24) {
    return { label: `Due in ${Math.max(1, Math.round(hours))}h`, fg: tints.warningText, bg: tints.warning };
  }
  const days = Math.round(hours / 24);
  return { label: `${days}d left`, fg: colors.textSecondary, bg: tints.neutral };
}

export function SlaCountdown({ dueAt, style }: SlaCountdownProps) {
  const s = classifySla(dueAt);
  return <span style={{ ...base, color: s.fg, background: s.bg, ...style }}>{s.label}</span>;
}

const base: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: '2px 10px',
  fontSize: '0.74rem',
  fontWeight: 600,
  borderRadius: 999,
  whiteSpace: 'nowrap',
};
